import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  DocumentIcon, 
  CloudArrowDownIcon, 
  TrashIcon,
  PlayIcon,
  FolderIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ViewColumnsIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { backupsAPI, serverAPI } from '../services/api';
import { format, isSameDay, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import BackupCalendar from '../components/BackupCalendar';
import SelectiveBackupModal from '../components/SelectiveBackupModal';

const Backups = () => {
  const [selectedType, setSelectedType] = useState('all');
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(null);
  const [collapsedDates, setCollapsedDates] = useState(new Set());
  const [showSelectiveModal, setShowSelectiveModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: backupsData, isLoading } = useQuery(
    ['backups', selectedType],
    () => backupsAPI.list({ type: selectedType === 'all' ? undefined : selectedType }),
    { refetchInterval: 30000 }
  );

  const downloadMutation = useMutation(backupsAPI.download, {
    onSuccess: (response) => {
      window.open(response.data.downloadUrl, '_blank');
    },
    onError: (error) => {
      alert('Erro ao gerar URL de download: ' + error.response?.data?.error);
    }
  });

  const deleteMutation = useMutation(backupsAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('backups');
      setShowConfirmDelete(null);
      alert('Backup deletado com sucesso!');
    },
    onError: (error) => {
      alert('Erro ao deletar backup: ' + error.response?.data?.error);
    }
  });

  const runBackupMutation = useMutation(serverAPI.runBackup, {
    onSuccess: () => {
      alert('Backup iniciado com sucesso!');
      setTimeout(() => {
        queryClient.invalidateQueries('backups');
      }, 5000);
    },
    onError: (error) => {
      alert('Erro ao executar backup: ' + error.response?.data?.error);
    }
  });

  const backups = backupsData?.data;

  const handleDownload = (key) => {
    downloadMutation.mutate(key);
  };

  const handleDelete = (key) => {
    deleteMutation.mutate(key);
  };

  const handleRunBackup = (type = 'all') => {
    runBackupMutation.mutate(type);
  };

  const getBackupsByType = () => {
    if (!backups) return [];
    
    let allBackups = [];
    
    if (selectedType === 'all') {
      allBackups = [
        ...backups.postgresql.map(b => ({ ...b, type: 'PostgreSQL' })),
        ...backups.portainer.map(b => ({ ...b, type: 'Portainer' })),
        ...backups.other.map(b => ({ ...b, type: 'Other' }))
      ];
    } else {
      const typeKey = selectedType === 'postgresql' ? 'postgresql' : 
                     selectedType === 'portainer' ? 'portainer' : 'other';
      
      allBackups = backups[typeKey]?.map(b => ({ 
        ...b, 
        type: selectedType.charAt(0).toUpperCase() + selectedType.slice(1) 
      })) || [];
    }
    
    // Filter by selected date if in calendar mode
    if (viewMode === 'calendar' && selectedDate) {
      allBackups = allBackups.filter(backup => 
        isSameDay(new Date(backup.lastModified), selectedDate)
      );
    }
    
    return allBackups.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
  };

  const getBackupsGroupedByDate = () => {
    const backupsList = getBackupsByType();
    const grouped = {};
    
    backupsList.forEach(backup => {
      const date = format(new Date(backup.lastModified), 'yyyy-MM-dd');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(backup);
    });
    
    return Object.entries(grouped)
      .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
      .map(([date, backups]) => ({ date, backups }));
  };

  const toggleDateCollapse = (date) => {
    const newCollapsed = new Set(collapsedDates);
    if (newCollapsed.has(date)) {
      newCollapsed.delete(date);
    } else {
      newCollapsed.add(date);
    }
    setCollapsedDates(newCollapsed);
  };

  const BackupCard = ({ backup, compact = false }) => {
    // Verificar se o backup é recente (últimos 10 minutos)
    const isRecent = (new Date() - new Date(backup.lastModified)) < 10 * 60 * 1000;
    
    return (
      <div className={`${compact ? 'bg-gray-50 border border-gray-200 rounded-lg p-3' : 'card'} ${isRecent ? 'ring-2 ring-green-200 bg-green-50' : ''}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 relative">
              <FolderIcon className={`${compact ? 'h-6 w-6' : 'h-8 w-8'} text-blue-500`} />
              {isRecent && (
                <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 rounded-full border-2 border-white">
                  <div className="h-full w-full bg-green-400 rounded-full animate-pulse"></div>
                </div>
              )}
            </div>
          
          <div className="min-w-0 flex-1">
            <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-gray-900 truncate`}>
              {backup.name}
            </p>
            <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500`}>
              {backup.type} • {backup.size}
            </p>
            <p className="text-xs text-gray-400">
              {compact 
                ? format(new Date(backup.lastModified), "dd/MM 'às' HH:mm", { locale: ptBR })
                : format(new Date(backup.lastModified), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
              }
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleDownload(backup.key)}
            disabled={downloadMutation.isLoading}
            className={`${compact ? 'btn-secondary text-xs py-1 px-2' : 'btn-secondary'} flex items-center space-x-1`}
          >
            <CloudArrowDownIcon className="h-4 w-4" />
            {!compact && <span className="hidden sm:inline">Download</span>}
          </button>
          
          <button
            onClick={() => setShowConfirmDelete(backup)}
            className={`${compact ? 'btn-danger text-xs py-1 px-2' : 'btn-danger'} flex items-center space-x-1`}
          >
            <TrashIcon className="h-4 w-4" />
            {!compact && <span className="hidden sm:inline">Deletar</span>}
          </button>
        </div>
      </div>
    </div>
    );
  };

  const DateGroup = ({ date, backups: dateBackups }) => {
    const isCollapsed = collapsedDates.has(date);
    const dateObj = parseISO(date + 'T00:00:00');
    
    return (
      <div className="card">
        <button
          onClick={() => toggleDateCollapse(date)}
          className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
        >
          <div className="flex items-center space-x-3">
            <CalendarIcon className="h-5 w-5 text-blue-500" />
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">
                {format(dateObj, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
              <p className="text-xs text-gray-500">
                {dateBackups.length} backup{dateBackups.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          {isCollapsed ? (
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>
        
        {!isCollapsed && (
          <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
            {dateBackups.map((backup, index) => (
              <BackupCard key={backup.key || index} backup={backup} compact={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  const typeButtons = [
    { key: 'all', label: 'Todos', count: backups ? backups.postgresql.length + backups.portainer.length + backups.other.length : 0 },
    { key: 'postgresql', label: 'PostgreSQL', count: backups?.postgresql?.length || 0 },
    { key: 'portainer', label: 'Portainer', count: backups?.portainer?.length || 0 },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Backups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Visualize, baixe e gerencie seus backups
          </p>
        </div>

        {/* Explicação dos tipos de backup */}
        <div className="mt-4 mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Tipos de Backup Disponíveis:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="flex items-start space-x-2">
              <CogIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Backup Seletivo</p>
                <p className="text-blue-700">Escolha bancos específicos e/ou Portainer</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <PlayIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900">Backup PostgreSQL</p>
                <p className="text-green-700">Apenas bancos de dados PostgreSQL configurados</p>
              </div>
            </div>
            <div className="flex items-start space-x-2">
              <PlayIcon className="h-4 w-4 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-purple-900">Backup Completo</p>
                <p className="text-purple-700">PostgreSQL + Portainer + configurações</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 sm:mt-0 flex flex-wrap gap-3">
          <button
            onClick={() => setShowSelectiveModal(true)}
            className="btn-primary flex items-center space-x-2 group relative"
            title="Abrir modal para selecionar bancos específicos e/ou Portainer"
          >
            <CogIcon className="h-4 w-4" />
            <span>Backup Seletivo</span>
          </button>
          
          <button
            onClick={() => handleRunBackup('postgresql')}
            disabled={runBackupMutation.isLoading}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors group relative"
            title="Executa backup apenas dos bancos PostgreSQL configurados no sistema"
          >
            <PlayIcon className="h-4 w-4" />
            <span>Backup PostgreSQL</span>
          </button>
          
          <button
            onClick={() => handleRunBackup('all')}
            disabled={runBackupMutation.isLoading}
            className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors group relative"
            title="Executa backup completo: PostgreSQL + Portainer + todas as configurações do sistema"
          >
            <PlayIcon className="h-4 w-4" />
            <span>Backup Completo</span>
          </button>
        </div>

        {/* Detalhes técnicos expansíveis */}
        <div className="mt-6 border-t pt-4">
          <details className="group">
            <summary className="cursor-pointer flex items-center text-sm text-gray-600 hover:text-gray-800">
              <ChevronDownIcon className="h-4 w-4 mr-1 group-open:rotate-180 transition-transform" />
              Detalhes técnicos dos backups
            </summary>
            <div className="mt-3 pl-5 space-y-3 text-sm text-gray-700">
              <div>
                <h4 className="font-medium text-gray-900">🎯 Backup Seletivo</h4>
                <ul className="ml-4 mt-1 space-y-1 text-xs">
                  <li>• Permite escolher bancos específicos do PostgreSQL</li>
                  <li>• Opção de incluir/excluir dados do Portainer</li>
                  <li>• Ideal para backups pontuais ou testes</li>
                  <li>• Controle granular sobre o que será incluído</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">🗄️ Backup PostgreSQL</h4>
                <ul className="ml-4 mt-1 space-y-1 text-xs">
                  <li>• Backup de todos os bancos configurados no sistema</li>
                  <li>• Inclui dados (data-only) e estruturas (schema-only)</li>
                  <li>• Gera backup consolidado quando configurado</li>
                  <li>• NÃO inclui Portainer nem outros serviços</li>
                  <li>• Upload automático para S3 se configurado</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">🚀 Backup Completo</h4>
                <ul className="ml-4 mt-1 space-y-1 text-xs">
                  <li>• Todos os bancos PostgreSQL configurados</li>
                  <li>• Backup completo dos dados do Portainer</li>
                  <li>• Configurações, stacks, certificados e volumes</li>
                  <li>• Compressão automática de arquivos</li>
                  <li>• Limpeza automática de backups antigos</li>
                  <li>• Notificação por email quando configurada</li>
                </ul>
              </div>
            </div>
          </details>
        </div>
      </div>

      {/* Filters and View Mode */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          {/* Type Filter */}
          <div className="flex flex-wrap gap-2">
            {typeButtons.map((type) => (
              <button
                key={type.key}
                onClick={() => setSelectedType(type.key)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-medium transition-colors
                  ${selectedType === type.key
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                  }
                `}
              >
                {type.label} ({type.count})
              </button>
            ))}
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`
                px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2
                ${viewMode === 'list'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                }
              `}
            >
              <ViewColumnsIcon className="h-4 w-4" />
              <span>Lista</span>
            </button>
            
            <button
              onClick={() => setViewMode('calendar')}
              className={`
                px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2
                ${viewMode === 'calendar'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
                }
              `}
            >
              <CalendarIcon className="h-4 w-4" />
              <span>Calendário</span>
            </button>
          </div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <BackupCalendar 
              backups={getBackupsByType()} 
              onDateSelect={setSelectedDate}
              selectedDate={selectedDate}
            />
          </div>
          
          <div className="lg:col-span-2">
            {selectedDate ? (
              <div className="space-y-4">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900">
                    Backups de {format(selectedDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                  </h3>
                </div>
                
                {getBackupsByType().length > 0 ? (
                  <div className="space-y-3">
                    {getBackupsByType().map((backup, index) => (
                      <BackupCard key={backup.key || index} backup={backup} />
                    ))}
                  </div>
                ) : (
                  <div className="card text-center py-8">
                    <CalendarIcon className="mx-auto h-8 w-8 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      Nenhum backup encontrado para esta data
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="card text-center py-12">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Selecione uma data</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Clique em uma data no calendário para ver os backups
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="card animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="h-8 w-8 bg-gray-200 rounded"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                    <div className="flex space-x-2">
                      <div className="h-8 w-20 bg-gray-200 rounded"></div>
                      <div className="h-8 w-20 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* Grouped by Date */}
              {getBackupsGroupedByDate().length > 0 ? (
                getBackupsGroupedByDate().map(({ date, backups: dateBackups }) => (
                  <DateGroup key={date} date={date} backups={dateBackups} />
                ))
              ) : (
                <div className="card text-center py-12">
                  <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum backup encontrado</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Execute um backup para ver os arquivos aqui.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Selective Backup Modal */}
      <SelectiveBackupModal 
        isOpen={showSelectiveModal}
        onClose={() => setShowSelectiveModal(false)}
      />

      {/* Confirm Delete Modal */}
      {showConfirmDelete && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <TrashIcon className="mx-auto h-12 w-12 text-red-500" />
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Confirmar Exclusão
              </h3>
              <p className="text-sm text-gray-500 mt-2">
                Tem certeza que deseja deletar o backup "{showConfirmDelete.name}"?
              </p>
              <p className="text-xs text-red-600 mt-1">
                Esta ação não pode ser desfeita.
              </p>
              
              <div className="flex justify-center space-x-4 mt-6">
                <button
                  onClick={() => setShowConfirmDelete(null)}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleDelete(showConfirmDelete.key)}
                  disabled={deleteMutation.isLoading}
                  className="btn-danger"
                >
                  {deleteMutation.isLoading ? 'Deletando...' : 'Deletar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Backups;